import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import walletService from '../../services/walletService';

const NETWORK_LABELS = {
  ETH: 'Ethereum (ERC-20)',
  BSC: 'BNB Smart Chain (BEP-20)',
  TRX: 'TRON (TRC-20)',
  SOL: 'Solana',
  BTC: 'Bitcoin',
  XRP: 'Ripple',
  DOGE: 'Dogecoin',
  LTC: 'Litecoin',
};

const NETWORK_COLORS = {
  ETH: '#627EEA', BSC: '#F3BA2F', TRX: '#FF0013',
  SOL: '#9945FF', BTC: '#F7931A', XRP: '#00AAE4',
  DOGE: '#C2A633', LTC: '#345D9D',
};

export default function DepositModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: choix coin, 2: choix réseau, 3: adresse
  const [supportedCoins, setSupportedCoins] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [addressInfo, setAddressInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCoins, setLoadingCoins] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [notifyForm, setNotifyForm] = useState({ amount: '', tx_hash: '', show: false });
  const [notifySuccess, setNotifySuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSupportedCoins();
      setStep(1);
      setSelectedCoin('');
      setSelectedNetwork('');
      setAddressInfo(null);
      setError('');
      setNotifySuccess(false);
      setNotifyForm({ amount: '', tx_hash: '', show: false });
    }
  }, [isOpen]);

  const loadSupportedCoins = async () => {
    try {
      setLoadingCoins(true);
      const coins = await walletService.getSupportedCoins();
      setSupportedCoins(coins);
    } catch (e) {
      // Fallback statique
      setSupportedCoins([
        { coin: 'USDT', networks: ['ETH', 'BSC', 'TRX', 'SOL'], minDeposit: '10' },
        { coin: 'BTC',  networks: ['BTC'],                        minDeposit: '0.0001' },
        { coin: 'ETH',  networks: ['ETH'],                        minDeposit: '0.01' },
        { coin: 'BNB',  networks: ['BSC'],                        minDeposit: '0.1' },
        { coin: 'SOL',  networks: ['SOL'],                        minDeposit: '0.1' },
        { coin: 'XRP',  networks: ['XRP'],                        minDeposit: '1' },
      ]);
    } finally {
      setLoadingCoins(false);
    }
  };

  const handleSelectCoin = (coin) => {
    setSelectedCoin(coin);
    setSelectedNetwork('');
    setAddressInfo(null);
    setError('');
    const coinData = supportedCoins.find(c => c.coin === coin);
    if (coinData?.networks?.length === 1) {
      handleSelectNetwork(coin, coinData.networks[0]);
    } else {
      setStep(2);
    }
  };

  const handleSelectNetwork = async (coin, network) => {
    setSelectedNetwork(network);
    setStep(3);
    setLoading(true);
    setError('');
    try {
      const info = await walletService.getDepositAddress(coin || selectedCoin, network);
      setAddressInfo(info);
    } catch (e) {
      setError(e.message || t('trading.error'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const handleNotify = async () => {
    if (!notifyForm.amount) return;
    try {
      setLoading(true);
      await walletService.notifyDeposit({
        coin: selectedCoin,
        network: selectedNetwork,
        amount: notifyForm.amount,
        tx_hash: notifyForm.tx_hash
      });
      setNotifySuccess(true);
      setNotifyForm({ amount: '', tx_hash: '', show: false });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const coinData = supportedCoins.find(c => c.coin === selectedCoin);
  const hasMemo = addressInfo?.tag && ['XRP', 'XLM', 'EOS', 'BNB'].includes(selectedCoin);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 9999, backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 18, fontWeight: 700 }}>
              💳 {t('wallet.deposit_crypto')}
            </h2>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
              {step === 1 && t('wallet.step_1')}
              {step === 2 && t('wallet.step_2', { coin: selectedCoin })}
              {step === 3 && t('wallet.step_3', { coin: selectedCoin })}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#64748b',
            cursor: 'pointer', fontSize: 20, padding: 4
          }}>✕</button>
        </div>

        <div style={{ padding: 24 }}>
          {/* ── ÉTAPE 1: Choix du coin ── */}
          {step === 1 && (
            <div>
              {loadingCoins ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
                  {t('wallet.loading_coins')}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {supportedCoins.map(({ coin, minDeposit }) => (
                    <button
                      key={coin}
                      onClick={() => handleSelectCoin(coin)}
                      style={{
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 12, padding: '16px 12px', cursor: 'pointer',
                        color: '#f1f5f9', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = '#6366f1'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
                    >
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{coin}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {t('wallet.min')}: {minDeposit} {coin}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ÉTAPE 2: Choix du réseau ── */}
          {step === 2 && coinData && (
            <div>
              <button onClick={() => setStep(1)} style={{
                background: 'none', border: 'none', color: '#6366f1',
                cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0
              }}>← {t('wallet.back')}</button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {coinData.networks.map(net => (
                  <button
                    key={net}
                    onClick={() => handleSelectNetwork(selectedCoin, net)}
                    style={{
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12, padding: '16px 20px', cursor: 'pointer',
                      color: '#f1f5f9', textAlign: 'left', display: 'flex',
                      alignItems: 'center', gap: 12, transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = NETWORK_COLORS[net] || '#6366f1'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
                  >
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: NETWORK_COLORS[net] || '#6366f1', flexShrink: 0
                    }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{net}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{NETWORK_LABELS[net] || net}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── ÉTAPE 3: Adresse de dépôt ── */}
          {step === 3 && (
            <div>
              <button onClick={() => { setStep(coinData?.networks?.length > 1 ? 2 : 1); setAddressInfo(null); }} style={{
                background: 'none', border: 'none', color: '#6366f1',
                cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0
              }}>← {t('wallet.back')}</button>

              {loading && (
                <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                  {t('trading.executing')}
                </div>
              )}

              {error && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 10, padding: 16, color: '#fca5a5', fontSize: 14, marginBottom: 16
                }}>⚠️ {error}</div>
              )}

              {addressInfo && !loading && (
                <div>
                  {/* Badge réseau */}
                  <div style={{
                    display: 'flex', justifyContent: 'center', marginBottom: 20
                  }}>
                    <span style={{
                      background: `${NETWORK_COLORS[selectedNetwork]}20`,
                      border: `1px solid ${NETWORK_COLORS[selectedNetwork]}50`,
                      color: NETWORK_COLORS[selectedNetwork] || '#e2e8f0',
                      padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600
                    }}>
                      {selectedCoin} — {NETWORK_LABELS[selectedNetwork] || selectedNetwork}
                    </span>
                  </div>

                  {/* Adresse */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>
                      {t('wallet.deposit_address')}
                    </label>
                    <div style={{
                      background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10, padding: '12px 16px',
                      display: 'flex', alignItems: 'center', gap: 10
                    }}>
                      <span style={{
                        flex: 1, fontFamily: 'monospace', fontSize: 13,
                        color: '#e2e8f0', wordBreak: 'break-all'
                      }}>{addressInfo.address}</span>
                      <button
                        onClick={() => copyToClipboard(addressInfo.address, 'address')}
                        style={{
                          background: copied === 'address' ? '#10b981' : '#6366f1',
                          border: 'none', borderRadius: 8, padding: '6px 12px',
                          color: '#fff', cursor: 'pointer', fontSize: 12, flexShrink: 0,
                          transition: 'background 0.2s'
                        }}
                      >
                        {copied === 'address' ? `✓ ${t('wallet.copied')}` : t('wallet.copy')}
                      </button>
                    </div>
                  </div>

                  {/* MEMO/TAG — CRITIQUE pour certains coins */}
                  {hasMemo && (
                    <div style={{
                      background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)',
                      borderRadius: 10, padding: 16, marginBottom: 16
                    }}>
                      <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 8, fontSize: 14 }}>
                        ⚠️ {t('wallet.memo_required')}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 10 }}>
                        {t('wallet.memo_warning')}
                      </div>
                      <div style={{
                        background: '#1e293b', borderRadius: 8, padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}>
                        <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 18, color: '#fbbf24', fontWeight: 700 }}>
                          {addressInfo.tag}
                        </span>
                        <button
                          onClick={() => copyToClipboard(addressInfo.tag, 'tag')}
                          style={{
                            background: copied === 'tag' ? '#10b981' : '#f59e0b',
                            border: 'none', borderRadius: 8, padding: '6px 12px',
                            color: '#fff', cursor: 'pointer', fontSize: 12,
                            transition: 'background 0.2s'
                          }}
                        >
                          {copied === 'tag' ? `✓ ${t('wallet.copied')}` : t('wallet.copy')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Instructions */}
                  <div style={{
                    background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
                    borderRadius: 10, padding: 16, marginBottom: 16
                  }}>
                    <div style={{ color: '#a5b4fc', fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                      📋 {t('wallet.instructions')}
                    </div>
                    {addressInfo.instructions && addressInfo.instructions.length > 0 ? (
                      addressInfo.instructions.map((line, i) => (
                        <div key={i} style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
                          • {line}
                        </div>
                      ))
                    ) : (
                      <>
                        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
                          • {t('wallet.instruction_only', { coin: selectedCoin, network: selectedNetwork })}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
                          • {t('wallet.instruction_min', { min: coinData?.minDeposit, coin: selectedCoin })}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 12 }}>
                          • {t('wallet.instruction_auto')}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Demo warning */}
                  {addressInfo.demo && (
                    <div style={{
                      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                      borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: '#fca5a5'
                    }}>
                      🔧 <strong>{t('wallet.demo_mode')}</strong> — BINANCE_API_KEY / BINANCE_API_SECRET config error.
                    </div>
                  )}

                  {/* Notification de dépôt */}
                  {notifySuccess ? (
                    <div style={{
                      background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                      borderRadius: 10, padding: 16, textAlign: 'center', color: '#34d399'
                    }}>
                      ✅ {t('wallet.deposit_notified')} {t('wallet.deposit_notified_desc')}
                    </div>
                  ) : !notifyForm.show ? (
                    <button
                      onClick={() => setNotifyForm(f => ({ ...f, show: true }))}
                      style={{
                        width: '100%', background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                        padding: '10px 16px', color: '#94a3b8', cursor: 'pointer', fontSize: 13
                      }}
                    >
                      {t('wallet.notify_deposit_cta')}
                    </button>
                  ) : (
                    <div style={{
                      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10, padding: 16
                    }}>
                      <div style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 12, fontSize: 14 }}>
                        {t('wallet.notify_deposit_title')}
                      </div>
                      <input
                        type="number"
                        placeholder={t('wallet.notify_amount', { min: coinData?.minDeposit, coin: selectedCoin })}
                        value={notifyForm.amount}
                        onChange={e => setNotifyForm(f => ({ ...f, amount: e.target.value }))}
                        style={{
                          width: '100%', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8, padding: '10px 14px', color: '#e2e8f0',
                          fontSize: 14, boxSizing: 'border-box', marginBottom: 10
                        }}
                      />
                      <input
                        type="text"
                        placeholder={t('wallet.notify_hash')}
                        value={notifyForm.tx_hash}
                        onChange={e => setNotifyForm(f => ({ ...f, tx_hash: e.target.value }))}
                        style={{
                          width: '100%', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8, padding: '10px 14px', color: '#e2e8f0',
                          fontFamily: 'monospace', fontSize: 12, boxSizing: 'border-box', marginBottom: 12
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => setNotifyForm(f => ({ ...f, show: false }))}
                          style={{
                            flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 8, padding: '10px', color: '#94a3b8', cursor: 'pointer'
                          }}
                        >{t('wallet.cancel')}</button>
                        <button
                          onClick={handleNotify}
                          disabled={!notifyForm.amount || loading}
                          style={{
                            flex: 2, background: '#6366f1', border: 'none',
                            borderRadius: 8, padding: '10px', color: '#fff',
                            cursor: 'pointer', fontWeight: 600, opacity: !notifyForm.amount ? 0.5 : 1
                          }}
                        >
                          {loading ? t('trading.executing') : `✓ ${t('wallet.confirm_deposit')}`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}